import * as React from "react";
import "flag-icons/css/flag-icons.min.css";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { telefonLaendervorwahl, type TelefonOptionen } from "@kmvs/forms-core";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { useFormPrimitiveStyles } from "../formPrimitives";
import { findPhoneCountry } from "../../../data/phoneCountries";
import { useTelefonFeld } from "./useTelefonFeld";

/**
 * Telefonfeld der Risikoanalyse im einheitlichen Format „+49 221 123456-78".
 *
 * Früher (PO-Spezifikation 18.08.2026) mit Länder-Dropdown links im Feld;
 * seit 09/2026 gilt app-weit EIN Feld, das immer mit „+" beginnt und in dem
 * die Ländervorwahl direkt editierbar ist (siehe useTelefonFeld). Die Flagge
 * bleibt als Anzeige: sie folgt der erkannten Ländervorwahl.
 *
 * Ein Bestandswert wird für die ANZEIGE formatiert, der gespeicherte Wert
 * bleibt unangetastet, bis der Nutzer das Feld anfasst.
 */

const useLocal = makeStyles({
  control: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    boxSizing: "border-box",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    transitionProperty: "border-color, box-shadow",
    transitionDuration: "140ms",
    transitionTimingFunction: "ease",
    ":hover": { ...shorthands.borderColor(REFINED.aub) },
    ":focus-within": {
      ...shorthands.borderColor(REFINED.aub),
      boxShadow: REFINED.focusRing,
    },
  },
  controlInvalid: { ...shorthands.borderColor(REFINED.err) },
  controlDisabled: { backgroundColor: REFINED.fieldBg, color: REFINED.ink3 },
  flag: { display: "inline-flex", alignItems: "center", paddingLeft: "12px", flexShrink: 0 },
  number: {
    flexGrow: 1,
    minWidth: "0px",
    ...shorthands.border("none"),
    backgroundColor: "transparent",
    ...shorthands.padding("11px", "13px"),
    fontFamily: REFINED.fontUI,
    fontWeight: 450,
    fontSize: "0.95rem",
    color: REFINED.ink,
    outlineStyle: "none",
    fontVariantNumeric: "tabular-nums",
    "::placeholder": { color: REFINED.ink3 },
  },
});

const ErrIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <circle cx="8" cy="8" r="6.4" />
    <path d="M8 4.8v3.6" strokeLinecap="round" />
    <circle cx="8" cy="11.1" r="0.5" fill="currentColor" stroke="none" />
  </svg>
);

export interface PhoneNumberInputProps {
  /** Beschriftung (ReactNode, damit der Host Zusätze wie den OCR-Chip mitgeben kann). */
  label: React.ReactNode;
  /** Klasse für das Label (Kontaktanlage „Refined" bzw. Risikoanalyse-Versalien). */
  labelClassName?: string;
  /** id/name des Nummernfeldes — das Label zeigt darauf. */
  name: string;
  /** Gespeicherte Schreibweise, z. B. "+49 221 12345". Leer heißt leeres Feld. */
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Von außen erzwungener Fehlerzustand (z. B. Pflichtfeld-Prüfung des Formulars). */
  invalid?: boolean;
  errorText?: string;
  /** Geschäftliche Nummer: Durchwahl „-" erlaubt. */
  geschaeftlich?: boolean;
  /** Zentralnummer(n) für die automatische Durchwahl. */
  zentrale?: TelefonOptionen["zentrale"];
}

export function PhoneNumberInput({
  label,
  labelClassName,
  name,
  value,
  onChange,
  required,
  disabled,
  placeholder = "+49 221 12345",
  invalid,
  errorText,
  geschaeftlich,
  zentrale,
}: PhoneNumberInputProps) {
  const s = useFormPrimitiveStyles();
  const l = useLocal();
  const [beruehrt, setBeruehrt] = React.useState(false);
  const feld = useTelefonFeld({
    value,
    onChange,
    geschaeftlich,
    zentrale,
    onBlur: () => setBeruehrt(true),
  });

  const cc = telefonLaendervorwahl(feld.anzeige);
  const land = cc ? findPhoneCountry(cc) : undefined;

  const eigeneMeldung = beruehrt ? feld.fehler : undefined;
  const zeigeFehler = (!!invalid && beruehrt) || !!eigeneMeldung;
  const meldung = eigeneMeldung ?? (invalid ? errorText : undefined);
  const hinweisId = `${name}-hinweis`;

  return (
    <div className={s.field}>
      <label className={mergeClasses(s.label, labelClassName)} htmlFor={name}>
        {label}
        {required && <span className={s.req}> *</span>}
      </label>

      <div className={mergeClasses(l.control, zeigeFehler && l.controlInvalid, disabled && l.controlDisabled)}>
        {land && (
          <span className={l.flag} title={land.name} aria-hidden="true">
            <span
              className={`fi fi-${land.code.toLowerCase()}`}
              style={{ width: 18, height: 13, borderRadius: 2, backgroundSize: "cover" }}
            />
          </span>
        )}
        <input
          {...feld.inputProps}
          id={name}
          name={name}
          className={l.number}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={zeigeFehler || undefined}
          aria-describedby={meldung ? hinweisId : undefined}
        />
      </div>

      {meldung && (
        <div className={s.errorMsg} id={hinweisId} role="alert">
          <ErrIcon />
          {meldung}
        </div>
      )}
    </div>
  );
}

export default PhoneNumberInput;
